import { useTranslation } from '@/shared/web/i18n/LanguageProvider';
import { useState } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { toast } from 'sonner';
import type { CreatorTicket } from '@shared/web/types';
import { Button } from '@/web/components/ui/button';
import { Textarea } from '@/web/components/ui/textarea';
import { creatorApi } from '../data/creator.api';

export function SupportFeedback({ ticket, onUpdated }: {
  ticket: CreatorTicket;
  onUpdated: (ticket: CreatorTicket) => void;
}) {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [writing, setWriting] = useState(false);
  const [sending, setSending] = useState(false);
  if (ticket.status !== 'resolved') return null;

  const submit = async (rating: 'up' | 'down') => {
    if (sending) return;
    setSending(true);
    try {
      const { ticket: updated } = await creatorApi.rateTicket(ticket.id, rating);
      onUpdated(updated);
      toast.success(t('Thanks for your rating'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('Could not save your rating'));
    } finally {
      setSending(false);
    }
  };

  const saveComment = async () => {
    if (!comment.trim() || sending) return;
    setSending(true);
    try {
      const { ticket: updated } = await creatorApi.addTicketFeedback(ticket.id, comment.trim());
      onUpdated(updated);
      setWriting(false);
      toast.success(t('Thanks for your feedback'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('Could not save your feedback'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border-border bg-card squircle flex flex-col gap-3 border p-4">
      {ticket.feedback ? (
        <>
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            {ticket.feedback.rating === 'up' ? <ThumbsUp className="size-4" /> : <ThumbsDown className="size-4" />}
            {t('Thanks for rating this support conversation.')}
          </p>
          {ticket.feedback.rating === 'down' && !ticket.feedback.comment && !writing && (
            <Button type="button" size="sm" variant="outline" className="self-start" onClick={() => setWriting(true)}>{t('Add feedback (optional)')}</Button>
          )}
          {ticket.feedback.rating === 'down' && !ticket.feedback.comment && writing && (
            <div className="flex flex-col gap-2">
              <Textarea aria-label={t('Optional feedback')} placeholder={t('What could we do better?')} maxLength={1000} value={comment} onChange={(event) => setComment(event.target.value)} rows={2} />
              <Button type="button" size="sm" className="self-end" disabled={sending || !comment.trim()} onClick={() => void saveComment()}>{t('Send feedback')}</Button>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="text-sm font-medium">{t('Was this helpful?')}</p>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" aria-label={t('Good support')} disabled={sending} onClick={() => void submit('up')}><ThumbsUp className="size-4" /> {t("Yes")}</Button>
            <Button type="button" size="sm" variant="outline" aria-label={t('Poor support')} disabled={sending} onClick={() => void submit('down')}><ThumbsDown className="size-4" /> {t("No")}</Button>
          </div>
        </>
      )}
    </div>
  );
}
